import * as THREE from 'three';
import { Text } from 'troika-three-text';

import { AssetManager } from '../../viewer/asset-manager';
import cameraManager from '../../viewer/camera-manager';
import { Cluster } from '../../viewer/elements/cluster';
import { TextBatch } from '../../viewer/elements/components/text-batch';
import { Poi } from '../../viewer/elements/poi/poi';
import { SkyView } from '../../viewer/skyview';
import { checkLine2dIntersection } from '../../viewer/utilities/math-helpers';
import { t } from '../i18-next';
import { bufferAttributeUtils } from './buffer-attribute';

/**
 * Normalize fontWeight to 'bold' or 'normal'
 */
function normalizeFontWeight(fontWeight?: string | number): 'bold' | 'normal' {
  if (!fontWeight) {
    return 'normal';
  }

  if (typeof fontWeight === 'number') {
    // CSS font-weight numeric values: 400 = normal, 700+ = bold
    return fontWeight >= 700 ? 'bold' : 'normal';
  }

  if (typeof fontWeight === 'string') {
    const lowerWeight = fontWeight.toLowerCase();
    // Check for bold variations
    if (
      lowerWeight === 'bold' ||
      lowerWeight === 'bolder' ||
      lowerWeight === '700' ||
      lowerWeight === '800' ||
      lowerWeight === '900'
    ) {
      return 'bold';
    }
    // Everything else is normal
    return 'normal';
  }

  return 'normal';
}

/**
 * Normalize textAnchor to 'start', 'middle', or 'end'
 */
function normalizeTextAnchor(anchorX?: Text['anchorX']): 'start' | 'middle' | 'end' {
  if (typeof anchorX === 'string') {
    const lowerAnchor = anchorX.toLowerCase();
    switch (lowerAnchor) {
      case 'left':
        return 'start';
      case 'center':
        return 'middle';
      case 'right':
        return 'end';
      default:
        return 'start';
    }
  }

  // For numeric anchorX, we'll use 'start' as default
  // The actual positioning will be handled by the numeric offset
  return 'start';
}

/**
 * Normalize dominantBaseline to 'text-before-edge', 'middle', or 'text-after-edge'
 */
function normalizeDominantBaseline(
  anchorY?: Text['anchorY'],
): 'text-before-edge' | 'middle' | 'text-after-edge' {
  if (typeof anchorY === 'string') {
    switch (anchorY) {
      case 'top':
      case 'top-cap':
      case 'top-ex':
        return 'text-before-edge';
      case 'middle':
        return 'middle';
      case 'bottom':
      case 'bottom-baseline':
        return 'text-after-edge';
      default:
        return 'text-before-edge';
    }
  }

  // For numeric anchorY, we'll use 'text-before-edge' as default
  // The actual positioning will be handled by the numeric offset
  return 'text-before-edge';
}

/**
 * Extract color from vertex colors or material, with minimal object creation
 */
function extractColor(
  colorArray: THREE.Color[] | null,
  material: THREE.Material | null,
  vertexIndex: number,
  useOriginalColor: boolean,
): string | undefined {
  if (!useOriginalColor) {
    return undefined;
  }

  if (colorArray && colorArray.length > vertexIndex) {
    return '#' + colorArray[vertexIndex].getHexString();
  }

  if (material && 'color' in material && material.color) {
    return '#' + (material.color as THREE.Color).getHexString();
  }

  return undefined;
}

/**
 * Project 3D point to SVG coordinates with minimal object creation
 * Reuses a single Vector3 to avoid repeated allocations
 */
function project3DToSVG(
  point3D: THREE.Vector3,
  camera: THREE.Camera,
  rendererWidth: number,
  rendererHeight: number,
  offsetX: number,
  offsetY: number,
  tempVector: THREE.Vector3, // Reused vector to avoid allocations
): { x: number; y: number } {
  tempVector.copy(point3D).project(camera);
  return {
    x: (tempVector.x * 0.5 + 0.5) * rendererWidth - offsetX,
    y: (0.5 - tempVector.y * 0.5) * rendererHeight - offsetY,
  };
}

/**
 * Check if geometry should be included in viewport with optimized culling
 */
function shouldIncludeInViewport(
  points: { x: number; y: number }[],
  svgWidth: number,
  svgHeight: number,
  tolerance: number,
  geometryType: 'line' | 'triangle' | 'point' | 'text',
): boolean {
  // Quick bounds check for points and text
  if (geometryType === 'point' || geometryType === 'text') {
    return isPointInViewportWithTolerance(points[0].x, points[0].y, svgWidth, svgHeight, tolerance);
  }

  // For lines and triangles, check if any point is in bounds
  const pointsInBounds = points.map((p) =>
    isPointInViewportWithTolerance(p.x, p.y, svgWidth, svgHeight, tolerance),
  );

  if (pointsInBounds.some((inBounds) => inBounds)) {
    return true;
  }

  // If no points are in bounds, check for intersection
  if (geometryType === 'line' && points.length >= 2) {
    return doesLineIntersectViewport(
      points[0].x,
      points[0].y,
      points[1].x,
      points[1].y,
      svgWidth,
      svgHeight,
    );
  }

  if (geometryType === 'triangle' && points.length >= 3) {
    return doesTriangleIntersectViewport(
      points[0].x,
      points[0].y,
      points[1].x,
      points[1].y,
      points[2].x,
      points[2].y,
      svgWidth,
      svgHeight,
    );
  }

  return false;
}

/**
 * Line data for SVG generation
 */
export type SVGLineData = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color?: string;
};

/**
 * Triangle data for SVG generation
 */
export type SVGTriangleData = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  x3: number;
  y3: number;
  color?: string;
  fill?: string;
};

/**
 * Text data for SVG generation
 */
export type SVGTextData = {
  x: number;
  y: number;
  text: string;
  fontSize: number;
  color?: string;
  fontFamily?: string;
  fontWeight?: 'bold' | 'normal';
  fontStyle?: string;
  anchorX?: number | string;
  anchorY?: number | string;
  rotation?: number;
  // SVG-specific attributes (calculated during vectorization)
  textAnchor?: 'start' | 'middle' | 'end';
  dominantBaseline?: 'text-before-edge' | 'middle' | 'text-after-edge';
};

/**
 * Point data for SVG generation
 */
export type SVGPointData = {
  x: number;
  y: number;
  color?: string;
  size?: number;
};

/**
 * POI data for SVG generation
 */
export type SVGPoiData = {
  x: number;
  y: number;
  thumbnailUrl?: string;
  clusterSize?: number;
  is360Poi?: boolean;
};

/**
 * Projects 3D geometry to 2D SVG coordinates and returns line, triangle, text, and point data
 */
export async function projectGeometryToSVG(
  assets: THREE.Object3D[],
  camera: THREE.Camera,
  renderer: THREE.WebGLRenderer,
  options?: {
    useOriginalLineColor?: boolean;
    useWhiteBackground?: boolean;
    width?: number;
    height?: number;
  },
  onProgress?: (progress: number, assetName?: string) => void,
): Promise<{
  lines: SVGLineData[];
  triangles: SVGTriangleData[];
  texts: SVGTextData[];
  points: SVGPointData[];
  pois: SVGPoiData[];
  width: number;
  height: number;
  backgroundColor?: string;
}> {
  const label = t('cadDrawingReport.vectorizing', { ns: 'skyviewReport' });
  const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
  const svgLines: SVGLineData[] = [];
  const svgTriangles: SVGTriangleData[] = [];
  const svgTexts: SVGTextData[] = [];
  const svgPoints: SVGPointData[] = [];
  const svgPois: SVGPoiData[] = [];
  const svgWidth = options?.width ?? renderer.domElement.width;
  const svgHeight = options?.height ?? renderer.domElement.height;

  // Calculate cropping offset if custom dimensions are provided
  const rendererWidth = renderer.domElement.width;
  const rendererHeight = renderer.domElement.height;
  const offsetX = (rendererWidth - svgWidth) / 2;
  const offsetY = (rendererHeight - svgHeight) / 2;

  // Reusable temp vector to avoid object creation in loops
  const tempVector = new THREE.Vector3();

  /**
   * Adds the text, placed by its matrix in world coordinates, if it is in the viewport.
   */
  const addSvgText = (object: Text, matrixWorld: THREE.Matrix4) => {
    // Get the text content
    const textContent = Array.isArray(object.text) ? object.text.join('\n') : object.text;
    if (!textContent || textContent.trim() === '') {
      return; // Skip empty text
    }

    // Apply object transformations to get world position
    const worldPosition = new THREE.Vector3().setFromMatrixPosition(matrixWorld);

    // Project to SVG coordinates using utility function
    const position = project3DToSVG(
      worldPosition,
      camera,
      rendererWidth,
      rendererHeight,
      offsetX,
      offsetY,
      tempVector,
    );

    // Check if text is within viewport bounds
    const tolerance = 100; // Allow text to extend slightly outside
    if (shouldIncludeInViewport([position], svgWidth, svgHeight, tolerance, 'text')) {
      // Calculate proper font size scaling from world units to screen pixels
      // The scaling should be inversely proportional to the camera's view size
      const viewportHeight = renderer.domElement.height;
      let worldScreenHeight: number;

      if (camera instanceof THREE.PerspectiveCamera) {
        // For perspective camera, calculate based on FOV and distance to origin
        const distanceToOrigin = camera.position.length();
        worldScreenHeight = 2 * Math.tan((camera.fov * Math.PI) / 360) * distanceToOrigin;
      } else if (camera instanceof THREE.OrthographicCamera) {
        // For orthographic camera, use the camera's height directly
        worldScreenHeight = camera.top - camera.bottom;
      } else {
        // Fallback for other camera types
        worldScreenHeight = 100; // Default value
      }

      // Invert the relationship: smaller worldScreenHeight (closer zoom) = larger text
      const pixelPerWorld = viewportHeight / worldScreenHeight;

      // Scale the original font size - larger pixelPerWorld means closer zoom = larger text
      const baseFontSize = object.fontSize || 1;
      const fontScale = pixelPerWorld * object.scale.x;
      const scaledFontSize = baseFontSize * fontScale; // Increased scale factor and lower minimum

      // Get world transformation data
      const worldQuaternion = new THREE.Quaternion();
      matrixWorld.decompose(new THREE.Vector3(), worldQuaternion, new THREE.Vector3());
      const worldRotation = new THREE.Euler().setFromQuaternion(worldQuaternion);

      // Calculate proper text positioning based on anchor settings
      // This handles both numeric offsets and string-based alignment
      let adjustedX = position.x;
      let adjustedY = position.y;

      // Calculate SVG-specific attributes using normalized functions
      const textAnchor = normalizeTextAnchor(object.anchorX);
      const dominantBaseline = normalizeDominantBaseline(object.anchorY);

      // Create adjustment vector
      const adjustment = new THREE.Vector2(0, 0);

      // Scale factor that aligns the texts correctly within the SVG viewport.
      const anchorScaleFactor = scaledFontSize * 0.24;

      // Handle anchorX positioning
      if (typeof object.anchorX === 'number') {
        // Numeric anchor - offset by the specified amount
        adjustment.x -= object.anchorX * anchorScaleFactor;
      } else if (object.anchorX === 'left') {
        // Subtract 5% of the font size to account for slightly wider text font.
        adjustment.x -= scaledFontSize * 0.05;
      }

      // Handle anchorY positioning
      if (typeof object.anchorY === 'number') {
        // Numeric anchor - offset by the specified amount
        adjustment.y += object.anchorY * anchorScaleFactor;
      } else if (object.anchorY === 'bottom-baseline') {
        adjustment.y += anchorScaleFactor;
      }

      // Rotate the adjustment vector according to the object's rotation
      adjustment.rotateAround(new THREE.Vector2(0, 0), -worldRotation.z);

      // Apply the rotated adjustments to the final position
      adjustedX += adjustment.x;
      adjustedY += adjustment.y;

      const textData: SVGTextData = {
        x: adjustedX,
        y: adjustedY,
        text: textContent,
        fontSize: scaledFontSize,
        fontFamily: object.font || 'Arial',
        fontWeight: normalizeFontWeight(object.fontWeight),
        fontStyle: object.fontStyle,
        anchorX: object.anchorX,
        anchorY: object.anchorY,
        rotation: -worldRotation.z, // Invert rotation direction
        // SVG-specific attributes (calculated above)
        textAnchor: textAnchor,
        dominantBaseline: dominantBaseline,
      };

      // Extract color if requested and available
      if (options?.useOriginalLineColor && object.color) {
        const color = new THREE.Color(object.color);
        textData.color = '#' + color.getHexString();
      }

      svgTexts.push(textData);
    }
  };

  let processedAssets = 0;
  const totalAssets = assets.length;

  // Traverse the scene to find compatible data for SVG.
  for (const asset of assets) {
    if (onProgress) {
      const progress = processedAssets / totalAssets;
      onProgress(progress, label);
      await nextFrame();
    }

    asset.traverse((object) => {
      if (
        object instanceof THREE.LineSegments &&
        object.visible &&
        object.geometry instanceof THREE.BufferGeometry
      ) {
        const positionArray = bufferAttributeUtils.getArray(object, true, 'position');
        const colorArray = bufferAttributeUtils.getColorArray(object);

        if (!positionArray) {
          return; // Skip if no position data
        }

        // Process line segments (pairs of vertices)
        for (let i = 0; i < positionArray.length - 1; i += 2) {
          const start3D = positionArray[i];
          const end3D = positionArray[i + 1];

          // Project to SVG coordinates using utility function
          const start = project3DToSVG(
            start3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );
          const end = project3DToSVG(
            end3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );

          // Check if line should be included in viewport
          const tolerance = 50;
          if (shouldIncludeInViewport([start, end], svgWidth, svgHeight, tolerance, 'line')) {
            const lineData: SVGLineData = {
              x1: start.x,
              y1: start.y,
              x2: end.x,
              y2: end.y,
            };

            // Extract color using utility function
            const color = extractColor(
              colorArray || null,
              object.material as THREE.Material,
              i,
              !!options?.useOriginalLineColor,
            );
            if (color) {
              lineData.color = color;
            }

            svgLines.push(lineData);
          }
        }
      }

      // Process Line objects (continuous lines/polylines) - but exclude LineSegments which are handled above
      if (
        object instanceof THREE.Line &&
        !(object instanceof THREE.LineSegments) &&
        object.visible &&
        object.geometry instanceof THREE.BufferGeometry
      ) {
        const positionArray = bufferAttributeUtils.getArray(object, true, 'position');
        const colorArray = bufferAttributeUtils.getColorArray(object);

        if (!positionArray) {
          return; // Skip if no position data
        }

        // Process continuous line (consecutive vertices are connected)
        for (let i = 0; i < positionArray.length - 1; i++) {
          const start3D = positionArray[i];
          const end3D = positionArray[i + 1];

          // Project to SVG coordinates using utility function
          const start = project3DToSVG(
            start3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );
          const end = project3DToSVG(
            end3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );

          // Check if line should be included in viewport
          const tolerance = 50;
          if (shouldIncludeInViewport([start, end], svgWidth, svgHeight, tolerance, 'line')) {
            const lineData: SVGLineData = {
              x1: start.x,
              y1: start.y,
              x2: end.x,
              y2: end.y,
            };

            // Extract color using utility function
            const color = extractColor(
              colorArray || null,
              object.material as THREE.Material,
              i,
              !!options?.useOriginalLineColor,
            );
            if (color) {
              lineData.color = color;
            }

            svgLines.push(lineData);
          }
        }
      }

      // Process Mesh objects for triangles (but exclude Troika text objects)
      if (
        object instanceof THREE.Mesh &&
        object.visible &&
        object.geometry instanceof THREE.BufferGeometry &&
        !(object instanceof Text)
      ) {
        // Get position and color arrays - getArray handles both indexed and non-indexed geometry
        const positionArray = bufferAttributeUtils.getArray(object, true, 'position');
        const colorArray = bufferAttributeUtils.getColorArray(object);

        if (!positionArray) {
          return; // Skip if no position data
        }

        // Process triangles (groups of 3 vertices)
        const triangleCount = positionArray.length / 3;
        for (let i = 0; i < triangleCount; i++) {
          const offset = i * 3;
          const pointA3D = positionArray[offset];
          const pointB3D = positionArray[offset + 1];
          const pointC3D = positionArray[offset + 2];

          // Project to SVG coordinates using utility function
          const pointA = project3DToSVG(
            pointA3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );
          const pointB = project3DToSVG(
            pointB3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );
          const pointC = project3DToSVG(
            pointC3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );

          // Check if triangle should be included in viewport
          const tolerance = 50;
          if (
            shouldIncludeInViewport(
              [pointA, pointB, pointC],
              svgWidth,
              svgHeight,
              tolerance,
              'triangle',
            )
          ) {
            const triangleData: SVGTriangleData = {
              x1: pointA.x,
              y1: pointA.y,
              x2: pointB.x,
              y2: pointB.y,
              x3: pointC.x,
              y3: pointC.y,
            };

            // Extract color using utility function
            const color = extractColor(
              colorArray || null,
              object.material as THREE.Material,
              offset,
              !!options?.useOriginalLineColor,
            );
            if (color) {
              triangleData.color = color;
              triangleData.fill = color;
            }

            svgTriangles.push(triangleData);
          }
        }
      }

      // Process Troika Text objects. The texts of a batch are not its children.
      if (object instanceof TextBatch && object.visible) {
        object.updateWorldMatrix(true, false);
        // As before a render, since e.g. labels have no size until they are drawn.
        object.updateTexts(renderer, camera);

        for (const text of object.texts) {
          if (text.visible) {
            addSvgText(text, object.getTextMatrixWorld(text));
          }
        }
      } else if (object instanceof Text && object.visible) {
        object.updateWorldMatrix(true, false);
        addSvgText(object, object.matrixWorld);
      }

      // Process Points objects
      if (
        object instanceof THREE.Points &&
        object.visible &&
        object.geometry instanceof THREE.BufferGeometry
      ) {
        const positionArray = bufferAttributeUtils.getArray(object, true, 'position');
        const colorArray = bufferAttributeUtils.getColorArray(object);

        if (!positionArray) {
          return; // Skip if no position data
        }

        // Process individual points
        for (let i = 0; i < positionArray.length; i++) {
          const point3D = positionArray[i];

          // Project to SVG coordinates using utility function
          const point = project3DToSVG(
            point3D,
            camera,
            rendererWidth,
            rendererHeight,
            offsetX,
            offsetY,
            tempVector,
          );

          // Check if point is within viewport bounds
          const tolerance = 10; // Allow points to extend slightly outside
          if (shouldIncludeInViewport([point], svgWidth, svgHeight, tolerance, 'point')) {
            const pointData: SVGPointData = {
              x: point.x,
              y: point.y,
            };

            // Extract color using utility function
            const color = extractColor(
              colorArray || null,
              object.material as THREE.Material,
              i,
              !!options?.useOriginalLineColor,
            );
            if (color) {
              pointData.color = color;
            }

            pointData.size = 0.5;
            svgPoints.push(pointData);
          }
        }
      }

      // Process Cluster objects
      if (
        (object instanceof Cluster && object.visible) ||
        (object instanceof Poi && object.hasLabel())
      ) {
        // Get the cluster position
        const clusterPosition = object.position.clone();

        // Project to SVG coordinates using utility function
        const position = project3DToSVG(
          clusterPosition,
          camera,
          rendererWidth,
          rendererHeight,
          offsetX,
          offsetY,
          tempVector,
        );

        // Check if cluster is within viewport bounds
        const tolerance = 100; // Allow clusters to extend slightly outside
        if (shouldIncludeInViewport([position], svgWidth, svgHeight, tolerance, 'point')) {
          const poiData: SVGPoiData = {
            x: position.x,
            y: position.y,
            clusterSize: object instanceof Cluster ? object.getClusterSize() : undefined,
            is360Poi: object instanceof Poi ? object.is360Poi() : undefined,
          };

          // Add thumbnail URL if available
          const thumbnailUrl =
            object instanceof Poi
              ? object.getThumbnailResourceUrl('64x_')
              : object.getThumbnailResourceUrl();

          if (thumbnailUrl) {
            poiData.thumbnailUrl = thumbnailUrl;
          }

          svgPois.push(poiData);
        }
      }
    });

    // Update progress and yield control to prevent UI blocking
    processedAssets++;
  }

  // Calculate background color
  let backgroundColor: string | undefined;
  if (options?.useWhiteBackground === false) {
    // Get the clear color from the renderer
    const clearColor = new THREE.Color();
    renderer.getClearColor(clearColor);
    backgroundColor = '#' + clearColor.getHexString();
  }

  return {
    lines: svgLines,
    triangles: svgTriangles,
    texts: svgTexts,
    points: svgPoints,
    pois: svgPois,
    width: svgWidth,
    height: svgHeight,
    backgroundColor,
  };
}

/**
 * Check if a point is within the viewport with tolerance
 */
function isPointInViewportWithTolerance(
  x: number,
  y: number,
  width: number,
  height: number,
  tolerance: number,
): boolean {
  return x >= -tolerance && x <= width + tolerance && y >= -tolerance && y <= height + tolerance;
}

/**
 * Check if a line intersects the viewport rectangle using existing math helpers
 */
function doesLineIntersectViewport(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  width: number,
  height: number,
): boolean {
  // Define viewport edges
  const viewportEdges = [
    // Top edge
    { start: new THREE.Vector3(0, 0, 0), end: new THREE.Vector3(width, 0, 0) },
    // Right edge
    { start: new THREE.Vector3(width, 0, 0), end: new THREE.Vector3(width, height, 0) },
    // Bottom edge
    { start: new THREE.Vector3(0, height, 0), end: new THREE.Vector3(width, height, 0) },
    // Left edge
    { start: new THREE.Vector3(0, 0, 0), end: new THREE.Vector3(0, height, 0) },
  ];

  const lineSegment = {
    start: new THREE.Vector3(x1, y1, 0),
    end: new THREE.Vector3(x2, y2, 0),
  };

  // Check if line intersects any viewport edge
  for (const edge of viewportEdges) {
    const intersection = checkLine2dIntersection(lineSegment, edge);
    if (intersection.onLine1 && intersection.onLine2) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a triangle intersects the viewport rectangle using existing math helpers
 */
function doesTriangleIntersectViewport(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  width: number,
  height: number,
): boolean {
  // Check if any triangle vertex is inside the viewport
  const vertices = [
    { x: x1, y: y1 },
    { x: x2, y: y2 },
    { x: x3, y: y3 },
  ];

  for (const vertex of vertices) {
    if (vertex.x >= 0 && vertex.x <= width && vertex.y >= 0 && vertex.y <= height) {
      return true;
    }
  }

  // Check if any triangle edge intersects the viewport
  if (doesLineIntersectViewport(x1, y1, x2, y2, width, height)) {
    return true;
  }
  if (doesLineIntersectViewport(x2, y2, x3, y3, width, height)) {
    return true;
  }
  if (doesLineIntersectViewport(x3, y3, x1, y1, width, height)) {
    return true;
  }

  return false;
}

/**
 * Generate SVG data from the current SkyView scene
 */
export async function generateSVGFromSkyView(
  skyview: SkyView,
  options?: {
    useOriginalLineColor?: boolean;
    useWhiteBackground?: boolean;
    width?: number;
    height?: number;
  },
  onProgress?: (progress: number, assetName?: string) => void,
): Promise<{
  lines: SVGLineData[];
  triangles: SVGTriangleData[];
  texts: SVGTextData[];
  points: SVGPointData[];
  pois: SVGPoiData[];
  width: number;
  height: number;
  backgroundColor?: string;
}> {
  const camera = cameraManager().getActiveCamera()?.getTHREECamera();
  const activeScene = skyview.getActiveScene?.() || skyview.baseScene;

  if (!camera || !activeScene) {
    throw new Error('Camera or active scene not available');
  }

  const visibleAssets = AssetManager.instance
    .getAssets(true)
    .filter((asset) => asset.getStateVisibility() && asset.loadingStatus === 'loaded');

  return await projectGeometryToSVG(visibleAssets, camera, skyview.renderer!, options, onProgress);
}
